import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command';
import { visibleSections, visibleActions } from '@/lib/navConfig';

export const CommandPalette = ({ user, open, onOpenChange, onAction }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const go = (item) => {
    onOpenChange(false);
    if (item.action) onAction?.(item.action);
    else navigate(item.to);
  };

  const pages = visibleSections(user).flatMap((s) => s.items.map((i) => ({ ...i, section: t(s.labelKey) })));

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('cmd.placeholder')} value={query} onValueChange={setQuery} data-testid="cmd-input" />
      <CommandList data-testid="cmd-list">
        <CommandEmpty>{t('cmd.empty')}</CommandEmpty>
        {query.trim() && (
          <CommandGroup>
            <CommandItem
              value={`search ${query}`}
              onSelect={() => { onOpenChange(false); navigate(`/search?q=${encodeURIComponent(query.trim())}`); }}
              data-testid="cmd-search-marketplace"
            >
              <Search className="mr-2 text-orange-500" />
              {t('cmd.searchFor', { q: query.trim() })}
            </CommandItem>
          </CommandGroup>
        )}
        <CommandGroup heading={t('cmd.actions')}>
          {visibleActions(user).map((a) => {
            const Icon = a.icon;
            return (
              <CommandItem key={a.key} value={`${t(a.labelKey)} ${a.key}`} onSelect={() => go(a)} data-testid={`cmd-action-${a.key}`}>
                <Icon className="mr-2 text-orange-500" />
                {t(a.labelKey)}
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading={t('cmd.pages')}>
          {pages.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem key={p.key} value={`${t(p.labelKey)} ${p.section} ${p.key}`} onSelect={() => go(p)} data-testid={`cmd-page-${p.key}`}>
                <Icon className={`mr-2 ${p.iconColor || 'text-muted-foreground'}`} />
                <span>{t(p.labelKey)}</span>
                <span className="ml-auto text-xs text-muted-foreground">{p.section}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
